/**
 * Patient lifecycle reporting hook (REP-001).
 *
 * Wraps the AI-gateway-hosted lifecycle report:
 *   - GET /api/v1/reports/patient-lifecycle?case_id= → PatientLifecycleReport
 *
 * The backend shape (services/ai_gateway/routes.py `PatientLifecycleReport`) is:
 *   { case_ref, timeline: [{at, event, stage}], duration_days,
 *     stage_breakdown: [{stage, days}] }
 * — a report (consent → close), not AI inference, so it is not disclosure-gated
 * but is still tenant-scoped (a case outside the caller's tenant returns an
 * empty report). We surface both the event timeline and the per-stage duration
 * breakdown, degrading on 404/empty to a deterministic seed.
 */
import { useCallback, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** A single event on the lifecycle timeline (REP-001). */
export interface LifecycleEvent {
  at: string;
  event: string;
  stage: string;
}

/** Per-stage duration breakdown (REP-001). */
export interface LifecycleStageDays {
  stage: string;
  days: number;
}

/** The composed patient-lifecycle report (REP-001). */
export interface PatientLifecycle {
  case_ref: string;
  timeline: LifecycleEvent[];
  duration_days: number;
  stage_breakdown: LifecycleStageDays[];
}

function seedLifecycle(caseRef: string): PatientLifecycle {
  const start = Date.now() - 1000 * 60 * 60 * 24 * 25;
  const day = 1000 * 60 * 60 * 24;
  return {
    case_ref: caseRef,
    duration_days: 25,
    timeline: [
      { at: new Date(start).toISOString(), event: 'Consent captured', stage: 'consent' },
      { at: new Date(start + 2 * day).toISOString(), event: 'Intake completed', stage: 'intake' },
      { at: new Date(start + 5 * day).toISOString(), event: 'Screening submitted', stage: 'screening' },
      { at: new Date(start + 9 * day).toISOString(), event: 'Employer verified', stage: 'employer' },
      { at: new Date(start + 18 * day).toISOString(), event: 'State application submitted', stage: 'application' },
      { at: new Date(start + 24 * day).toISOString(), event: 'Enrollment confirmed', stage: 'enrolled' },
      { at: new Date(start + 25 * day).toISOString(), event: 'Case closed', stage: 'closed' },
    ],
    stage_breakdown: [
      { stage: 'consent', days: 0 },
      { stage: 'intake', days: 2 },
      { stage: 'screening', days: 3 },
      { stage: 'employer', days: 4 },
      { stage: 'application', days: 9 },
      { stage: 'enrolled', days: 6 },
      { stage: 'closed', days: 1 },
    ],
  };
}

interface RawLifecycle {
  case_ref?: string;
  timeline?: { at?: string; event?: string; stage?: string }[];
  duration_days?: number;
  stage_breakdown?: { stage?: string; days?: number }[];
}

function adapt(caseRef: string, raw: RawLifecycle | null | undefined): PatientLifecycle {
  if (!raw || typeof raw !== 'object') return seedLifecycle(caseRef);
  const timeline = raw.timeline ?? [];
  const breakdown = raw.stage_breakdown ?? [];
  // A tenant-mismatch / unknown case returns an empty report — seed it so the
  // demo always shows the full design rather than a blank report.
  if (!timeline.length && !breakdown.length) return seedLifecycle(caseRef);
  return {
    case_ref: raw.case_ref ?? caseRef,
    duration_days: raw.duration_days ?? breakdown.reduce((a, s) => a + (s.days ?? 0), 0),
    timeline: timeline.map((e) => ({ at: e.at ?? '', event: e.event ?? '—', stage: e.stage ?? '' })),
    stage_breakdown: breakdown.map((s) => ({ stage: s.stage ?? '—', days: s.days ?? 0 })),
  };
}

interface UseLifecycleResult {
  lifecycle: PatientLifecycle | null;
  available: boolean;
  isLoading: boolean;
  error: ApiError | null;
  load: (caseId: string) => void;
}

/** Loads a patient-lifecycle report on demand (REP-001), seeding on 404/empty. */
export function useLifecycle(): UseLifecycleResult {
  const [lifecycle, setLifecycle] = useState<PatientLifecycle | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback((caseId: string) => {
    const cid = caseId.trim();
    if (!cid) return;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<RawLifecycle>(`/reports/patient-lifecycle?case_id=${encodeURIComponent(cid)}`)
      .then((data) => {
        const hasData = !!(data?.timeline?.length || data?.stage_breakdown?.length);
        setLifecycle(adapt(cid, data));
        setAvailable(hasData);
      })
      .catch((err: unknown) => {
        setLifecycle(seedLifecycle(cid));
        setAvailable(false);
        if (err instanceof ApiError && err.status !== 404) setError(err);
      })
      .finally(() => setIsLoading(false));
  }, []);

  return { lifecycle, available, isLoading, error, load };
}
